import { useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  useGetMailFullSyncStatus,
  useStartMailFullSync,
  usePauseMailFullSync,
  useResumeMailFullSync,
  useCancelMailFullSync,
  useListMailFullSyncHistory,
  getGetMailFullSyncStatusQueryKey,
  getListMailAccountsQueryKey,
  getListMailFoldersQueryKey,
  getListMailMessagesQueryKey,
  getListMailFullSyncHistoryQueryKey,
  type MailFullSyncJob,
  type MailFullSyncHistoryEntry,
  type StartMailFullSyncBodyDepth,
} from "@workspace/api-client-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import {
  History,
  Pause,
  Play,
  X,
  RefreshCw,
  AlertTriangle,
  Loader2,
  ChevronDown,
  CheckCircle2,
  XCircle,
  MinusCircle,
} from "lucide-react";

const DEPTH_LABELS: Record<StartMailFullSyncBodyDepth, string> = {
  "3m": "3 derniers mois",
  "12m": "12 derniers mois",
  all: "Tout l'historique",
  y2020: "Depuis le 1ᵉʳ janvier 2020",
  y2021: "Depuis le 1ᵉʳ janvier 2021",
  y2022: "Depuis le 1ᵉʳ janvier 2022",
  y2023: "Depuis le 1ᵉʳ janvier 2023",
  y2024: "Depuis le 1ᵉʳ janvier 2024",
  y2025: "Depuis le 1ᵉʳ janvier 2025",
  y2026: "Depuis le 1ᵉʳ janvier 2026",
  y2027: "Depuis le 1ᵉʳ janvier 2027",
  y2028: "Depuis le 1ᵉʳ janvier 2028",
  y2029: "Depuis le 1ᵉʳ janvier 2029",
  y2030: "Depuis le 1ᵉʳ janvier 2030",
};

/** Relative-range options shown as radios above the year picker. */
const RELATIVE_DEPTHS: StartMailFullSyncBodyDepth[] = ["3m", "12m", "all"];

/**
 * Year-based options surfaced in the dialog: from 2020 up to the current
 * calendar year (so the user never sees a "future" choice). Computed once at
 * module load — that's plenty fresh given page reloads, and avoids a new
 * array reference on every render.
 */
const YEAR_DEPTHS: StartMailFullSyncBodyDepth[] = (() => {
  const currentYear = new Date().getFullYear();
  const out: StartMailFullSyncBodyDepth[] = [];
  for (let y = currentYear; y >= 2020; y--) {
    const key = `y${y}` as StartMailFullSyncBodyDepth;
    if (key in DEPTH_LABELS) out.push(key);
  }
  return out;
})();

/**
 * Single ordered list of period choices for the dropdown: recent relative
 * ranges first, then each year (desc), then the full history. One clear menu
 * instead of a radio-group + year-select combo.
 */
const DEPTH_OPTIONS: StartMailFullSyncBodyDepth[] = ["3m", "12m", ...YEAR_DEPTHS, "all"];

function isYearDepth(value: StartMailFullSyncBodyDepth): boolean {
  return value.startsWith("y");
}

function defaultYearDepth(): StartMailFullSyncBodyDepth {
  // First entry is the most recent year ≤ current.
  return YEAR_DEPTHS[0] ?? "y2026";
}

function depthLabel(depth: string): string {
  return DEPTH_LABELS[depth as StartMailFullSyncBodyDepth] ?? depth;
}

interface Props {
  accountId: string;
}

/**
 * Per-account "full history sync" controls. Renders the button/dialog used
 * to start a deep sync, plus the live progress block (bar, status, pause /
 * resume / cancel buttons, retry on error). The block only appears when a
 * job exists for the account.
 *
 * Real-time updates come from the SSE stream installed once at the page
 * level; the listener invalidates this hook's query so we re-fetch the
 * authoritative status from the server. We intentionally don't try to
 * reconstruct the snapshot from the SSE payload — the server always wins.
 */
export default function MailFullSyncControls({ accountId }: Props) {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [depth, setDepth] = useState<StartMailFullSyncBodyDepth>("3m");
  // Default ON: existing behaviour is to triage everything we download.
  // Users uncheck this when they want a pure download (no AI cap usage)
  // — typically for very large historical backfills.
  const [triageEnabled, setTriageEnabled] = useState<boolean>(true);
  const [historyOpen, setHistoryOpen] = useState(false);

  const statusQuery = useGetMailFullSyncStatus(accountId, {
    query: {
      queryKey: getGetMailFullSyncStatusQueryKey(accountId),
      // Belt-and-suspenders refresh in case SSE drops (matches the rest of
      // the mail UI's 30s safety net).
      refetchInterval: (query) => {
        const data = query.state.data as MailFullSyncJob | void;
        if (!data) return false;
        return data.status === "running" || data.status === "paused" ? 5_000 : false;
      },
    },
  });

  const job = statusQuery.data ?? null;

  const historyQuery = useListMailFullSyncHistory(
    accountId,
    { limit: 5 },
    {
      query: {
        queryKey: getListMailFullSyncHistoryQueryKey(accountId, { limit: 5 }),
        // Refetch when the panel is opened — cheap, single small query.
        enabled: historyOpen,
      },
    },
  );
  const history = historyQuery.data?.items ?? [];

  // ── SSE listener ────────────────────────────────────────────────────────
  useEffect(() => {
    const es = new EventSource("/api/mail/events");
    const refresh = (event: MessageEvent<string>) => {
      try {
        const payload = JSON.parse(event.data) as { accountId?: string };
        if (payload.accountId !== accountId) return;
      } catch {
        return;
      }
      void qc.invalidateQueries({
        queryKey: getGetMailFullSyncStatusQueryKey(accountId),
      });
      void qc.invalidateQueries({
        queryKey: getListMailFoldersQueryKey(accountId),
      });
      // Progressive mail-list refresh: as the worker streams batches into
      // `mail_messages`, the inbox/folder views should pick them up. Orval's
      // query keys are arrays prefixed with the accountId, so passing the
      // partial key invalidates every (folder/limit/offset/...) variant
      // currently mounted for this account.
      void qc.invalidateQueries({
        queryKey: getListMailMessagesQueryKey(accountId),
      });
    };
    es.addEventListener("mail:fullsync:progress", refresh);
    es.addEventListener("mail:fullsync:done", (event) => {
      refresh(event as MessageEvent<string>);
      void qc.invalidateQueries({ queryKey: getListMailAccountsQueryKey() });
      // A terminal event means a fresh snapshot landed in history.
      void qc.invalidateQueries({
        queryKey: getListMailFullSyncHistoryQueryKey(accountId, { limit: 5 }),
      });
    });
    es.addEventListener("mail:fullsync:error", (event) => {
      refresh(event as MessageEvent<string>);
      void qc.invalidateQueries({
        queryKey: getListMailFullSyncHistoryQueryKey(accountId, { limit: 5 }),
      });
    });
    return () => {
      es.close();
    };
  }, [accountId, qc]);

  // ── Mutations ───────────────────────────────────────────────────────────
  const start = useStartMailFullSync({
    mutation: {
      onSuccess: () => {
        setDialogOpen(false);
        toast({ title: "Synchronisation complète lancée" });
        void qc.invalidateQueries({
          queryKey: getGetMailFullSyncStatusQueryKey(accountId),
        });
      },
      onError: (err: Error) =>
        toast({
          title: "Impossible de lancer la synchro complète",
          description: err.message,
          variant: "destructive",
        }),
    },
  });

  const pause = usePauseMailFullSync({
    mutation: {
      onSuccess: () =>
        void qc.invalidateQueries({
          queryKey: getGetMailFullSyncStatusQueryKey(accountId),
        }),
    },
  });
  const resume = useResumeMailFullSync({
    mutation: {
      onSuccess: () =>
        void qc.invalidateQueries({
          queryKey: getGetMailFullSyncStatusQueryKey(accountId),
        }),
    },
  });
  const cancel = useCancelMailFullSync({
    mutation: {
      onSuccess: () =>
        void qc.invalidateQueries({
          queryKey: getGetMailFullSyncStatusQueryKey(accountId),
        }),
    },
  });

  const isActive = job?.status === "running" || job?.status === "paused";
  const isTerminal =
    job?.status === "completed" ||
    job?.status === "failed" ||
    job?.status === "cancelled";

  return (
    <div className="basis-full space-y-2">
      <div className="flex items-center gap-2 min-w-0">
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button
              size="sm"
              variant="outline"
              disabled={isActive}
              data-testid={`button-full-sync-${accountId}`}
              className="shrink-0"
            >
              <History className="w-3.5 h-3.5 mr-1.5" />
              Tout synchroniser
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Synchroniser tout l'historique</DialogTitle>
              <DialogDescription>
                Choisis la profondeur. La synchro tourne en arrière-plan, tu
                peux la mettre en pause à tout moment.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-1.5">
              <Label htmlFor={`depth-select-${accountId}`} className="text-sm">
                Jusqu'où remonter dans l'historique&nbsp;?
              </Label>
              <Select
                value={depth}
                onValueChange={(v) => setDepth(v as StartMailFullSyncBodyDepth)}
              >
                <SelectTrigger
                  id={`depth-select-${accountId}`}
                  className="w-full"
                  data-testid="select-full-sync-depth"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DEPTH_OPTIONS.map((value) => (
                    <SelectItem key={value} value={value}>
                      {DEPTH_LABELS[value]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Plus tu remontes loin, plus la synchro est longue. Tu pourras la
                mettre en pause à tout moment.
              </p>
            </div>
            <div className="flex items-start gap-2 pt-2">
              <Checkbox
                id={`triage-${accountId}`}
                checked={triageEnabled}
                onCheckedChange={(v) => setTriageEnabled(v === true)}
                data-testid="checkbox-full-sync-triage"
              />
              <div className="space-y-1">
                <Label
                  htmlFor={`triage-${accountId}`}
                  className="text-sm cursor-pointer"
                >
                  Analyser ces mails avec l'IA
                </Label>
                <p className="text-xs text-muted-foreground">
                  Décochez pour seulement télécharger les mails, sans analyse
                  IA. Les règles automatiques s'appliquent toujours.
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="ghost"
                onClick={() => setDialogOpen(false)}
                disabled={start.isPending}
              >
                Annuler
              </Button>
              <Button
                onClick={() =>
                  start.mutate({
                    id: accountId,
                    data: { depth, triageEnabled },
                  })
                }
                disabled={start.isPending}
              >
                {start.isPending ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : null}
                Lancer la synchro
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {isTerminal && job ? (
          <span className="text-xs text-muted-foreground min-w-0 truncate flex-1">
            {summarizeTerminalStatus(job)}
          </span>
        ) : null}
      </div>

      {isActive && job ? (
        <ActiveProgress
          job={job}
          onPause={() => pause.mutate({ id: accountId })}
          onResume={() => resume.mutate({ id: accountId })}
          onCancel={() => cancel.mutate({ id: accountId })}
          pausePending={pause.isPending}
          resumePending={resume.isPending}
          cancelPending={cancel.isPending}
        />
      ) : null}

      {job?.status === "failed" ? (
        <div className="rounded-lg border border-destructive/40 bg-destructive/5 p-3 text-xs space-y-2">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-destructive mt-0.5 shrink-0" />
            <div className="min-w-0">
              <p className="font-medium text-destructive">
                Synchro complète interrompue
              </p>
              <p className="text-muted-foreground mt-0.5 break-words">
                {job.lastError ?? "Erreur inconnue."}
              </p>
            </div>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={() =>
              start.mutate({
                id: accountId,
                // Preserve the user's original AI-triage choice on retry
                // — silently re-enabling AI on a job they explicitly
                // opted out of would burn their daily cap unexpectedly.
                data: {
                  depth: job.depth as StartMailFullSyncBodyDepth,
                  triageEnabled: job.triageEnabled,
                },
              })
            }
            disabled={start.isPending}
            data-testid={`button-retry-full-sync-${accountId}`}
          >
            <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
            Réessayer
          </Button>
        </div>
      ) : null}

      <Collapsible
        open={historyOpen}
        onOpenChange={setHistoryOpen}
        className="rounded-lg border border-border bg-card/30"
      >
        <CollapsibleTrigger asChild>
          <button
            type="button"
            className="w-full flex items-center justify-between px-3 py-2 text-xs text-muted-foreground hover:text-foreground transition-colors"
            data-testid={`fullsync-history-toggle-${accountId}`}
          >
            <span className="flex items-center gap-1.5">
              <History className="w-3.5 h-3.5" />
              Historique des synchros
            </span>
            <ChevronDown
              className={`w-3.5 h-3.5 transition-transform ${historyOpen ? "rotate-180" : ""}`}
            />
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent className="px-3 pb-3 pt-0 space-y-1.5">
          {historyQuery.isLoading ? (
            <p className="text-xs text-muted-foreground py-2">Chargement…</p>
          ) : history.length === 0 ? (
            <p className="text-xs text-muted-foreground py-2">
              Aucune synchro complète terminée pour ce compte.
            </p>
          ) : (
            <ul className="space-y-1.5">
              {history.map((entry) => (
                <HistoryRow key={entry.id} entry={entry} />
              ))}
            </ul>
          )}
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}

/**
 * Sliding-window ETA estimator. Keeps the last ~12 (timestamp, downloaded)
 * samples and computes a recent rate from them. We intentionally cap the
 * window so transient slowdowns/spikes don't poison the estimate forever.
 *
 * Reset semantics:
 *   - When the job is paused, we drop in-flight samples so the rate
 *     restarts from the resume point.
 *   - When totalDownloaded decreases (rare — would only happen if a job is
 *     restarted), we also reset.
 */
function useEtaEstimate(job: MailFullSyncJob): string | null {
  const samples = useRef<Array<{ t: number; n: number }>>([]);
  const lastDownloaded = useRef(0);
  const lastStatus = useRef(job.status);

  const total = Math.max(job.totalEstimated, job.totalDownloaded);
  const remaining = Math.max(0, total - job.totalDownloaded);

  useEffect(() => {
    // Reset on pause or on a counter regression.
    if (
      job.status === "paused" ||
      job.totalDownloaded < lastDownloaded.current ||
      (lastStatus.current === "paused" && job.status === "running")
    ) {
      samples.current = [];
    }
    lastDownloaded.current = job.totalDownloaded;
    lastStatus.current = job.status;
    if (job.status !== "running") return;
    const now = Date.now();
    const arr = samples.current;
    arr.push({ t: now, n: job.totalDownloaded });
    while (arr.length > 12) arr.shift();
  }, [job.status, job.totalDownloaded]);

  return useMemo(() => {
    if (job.status !== "running") return null;
    if (remaining === 0) return null;
    const arr = samples.current;
    if (arr.length < 2) return null;
    const first = arr[0];
    const last = arr[arr.length - 1];
    const dt = (last.t - first.t) / 1000; // seconds
    const dn = last.n - first.n;
    if (dt <= 0 || dn <= 0) return null;
    const rate = dn / dt; // mails/sec
    const secondsLeft = remaining / rate;
    return formatEta(secondsLeft);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job.status, job.totalDownloaded, total, remaining]);
}

function formatEta(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return "";
  if (seconds < 60) return "≈ moins d'1 min restantes";
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `≈ ${minutes} min restantes`;
  const hours = Math.floor(minutes / 60);
  const remMin = minutes % 60;
  if (hours < 24) {
    return remMin === 0
      ? `≈ ${hours} h restantes`
      : `≈ ${hours} h ${remMin} min restantes`;
  }
  const days = Math.floor(hours / 24);
  const remHours = hours % 24;
  return remHours === 0
    ? `≈ ${days} j restant${days > 1 ? "s" : ""}`
    : `≈ ${days} j ${remHours} h restant${days > 1 ? "s" : ""}`;
}

function ActiveProgress(props: {
  job: MailFullSyncJob;
  onPause: () => void;
  onResume: () => void;
  onCancel: () => void;
  pausePending: boolean;
  resumePending: boolean;
  cancelPending: boolean;
}) {
  const { job } = props;
  // Only show a percentage when the server has a trustworthy estimate.
  // Otherwise total == downloaded ⇒ the bar would jump to ~100% instantly,
  // which is exactly the misleading behaviour we want to avoid.
  const hasEstimate = job.totalEstimated > 0;
  const value = hasEstimate
    ? Math.min(100, Math.round((job.totalDownloaded / job.totalEstimated) * 100))
    : null;
  const isPaused = job.status === "paused";
  const downloadedFmt = job.totalDownloaded.toLocaleString("fr-FR");
  const totalFmt = job.totalEstimated.toLocaleString("fr-FR");
  const folderSuffix = job.currentFolderPath
    ? ` — dossier « ${job.currentFolderPath} »`
    : "";
  const countText = hasEstimate
    ? `${downloadedFmt} / ${totalFmt} mails`
    : `${downloadedFmt} mails`;
  const summary = isPaused
    ? `En pause — ${countText}${folderSuffix}`
    : hasEstimate
      ? `Synchronisation : ${countText}${folderSuffix}`
      : `Synchronisation en cours — ${countText} récupérés${folderSuffix}`;
  const eta = useEtaEstimate(job);

  return (
    <div className="rounded-lg border border-border bg-card/40 p-3 space-y-2">
      <div className="flex items-start justify-between gap-2">
        <p className="text-xs text-foreground flex-1 min-w-0 break-words" data-testid={`fullsync-progress-label-${job.accountId}`}>
          {summary}
        </p>
        <div className="flex items-center gap-1 shrink-0">
          {isPaused ? (
            <Button
              size="sm"
              variant="ghost"
              onClick={props.onResume}
              disabled={props.resumePending}
              data-testid={`button-resume-full-sync-${job.accountId}`}
            >
              <Play className="w-3.5 h-3.5 mr-1" /> Reprendre
            </Button>
          ) : (
            <Button
              size="sm"
              variant="ghost"
              onClick={props.onPause}
              disabled={props.pausePending}
              data-testid={`button-pause-full-sync-${job.accountId}`}
            >
              <Pause className="w-3.5 h-3.5 mr-1" /> Pause
            </Button>
          )}
          <Button
            size="sm"
            variant="ghost"
            className="text-destructive hover:text-destructive"
            onClick={props.onCancel}
            disabled={props.cancelPending}
            data-testid={`button-cancel-full-sync-${job.accountId}`}
          >
            <X className="w-3.5 h-3.5 mr-1" /> Arrêter
          </Button>
        </div>
      </div>
      {value !== null ? (
        <Progress value={value} className="h-1.5" />
      ) : (
        // Total inconnu : barre « indéterminée » (pulsation) plutôt qu'un
        // pourcentage faux.
        <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
          <div className="h-full w-full bg-primary/60 animate-pulse" />
        </div>
      )}
      {eta ? (
        <p
          className="text-[11px] text-muted-foreground"
          data-testid={`fullsync-eta-${job.accountId}`}
        >
          {eta}
        </p>
      ) : null}
    </div>
  );
}

function HistoryRow({ entry }: { entry: MailFullSyncHistoryEntry }) {
  const finished = new Date(entry.finishedAt);
  const finishedLabel = finished.toLocaleString("fr-FR", {
    dateStyle: "short",
    timeStyle: "short",
  });
  const downloaded = entry.totalDownloaded.toLocaleString("fr-FR");
  const Icon =
    entry.status === "completed"
      ? CheckCircle2
      : entry.status === "failed"
        ? XCircle
        : MinusCircle;
  const iconClass =
    entry.status === "completed"
      ? "text-blue-500"
      : entry.status === "failed"
        ? "text-destructive"
        : "text-muted-foreground";
  const statusLabel =
    entry.status === "completed"
      ? "Terminée"
      : entry.status === "failed"
        ? "Échouée"
        : "Annulée";

  return (
    <li
      className="flex items-start gap-2 text-xs"
      data-testid={`fullsync-history-row-${entry.id}`}
    >
      <Icon className={`w-3.5 h-3.5 mt-0.5 shrink-0 ${iconClass}`} />
      <div className="min-w-0 flex-1">
        <p className="text-foreground">
          <span className="font-medium">{statusLabel}</span>
          {" · "}
          <span className="text-muted-foreground">{depthLabel(entry.depth)}</span>
          {" · "}
          <span>{downloaded} mails</span>
        </p>
        <p className="text-[11px] text-muted-foreground">
          {finishedLabel}
          {entry.lastError ? ` — ${entry.lastError}` : ""}
        </p>
      </div>
    </li>
  );
}

function summarizeTerminalStatus(job: MailFullSyncJob): string {
  if (job.status === "completed") {
    // 0 téléchargé = tout était déjà synchronisé (cas le plus fréquent quand on
    // relance). Ne pas afficher « 0 mails », qui laisse croire à un échec.
    if (job.totalDownloaded === 0) {
      return "✓ Boîte déjà à jour — aucun nouveau mail à télécharger";
    }
    const dateSuffix = job.completedAt
      ? ` le ${new Date(job.completedAt).toLocaleDateString("fr-FR")}`
      : "";
    return `✓ ${job.totalDownloaded.toLocaleString("fr-FR")} mails synchronisés${dateSuffix}`;
  }
  if (job.status === "cancelled") {
    return "Synchro complète annulée";
  }
  return "";
}
